import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Icon, UiButton, UiText } from '@shared/ui';

import cls from './HintTooltip.module.css';
import { BackdropOptions, HintBehavior } from '../../config/hintsConfig';

interface HintTooltipProps {
    text: string;
    behavior: HintBehavior;
    targetEl: Element;
    position: 'top' | 'right' | 'bottom' | 'left';
    onDismiss: () => void;
    onAction: () => void;
    backdropOptions?: BackdropOptions;
}

const GAP = 16;
const MARGIN = 8;

export const HintTooltip = ({
    text,
    behavior,
    targetEl,
    position,
    onDismiss,
    onAction,
    backdropOptions,
}: HintTooltipProps) => {
    const { t } = useTranslation();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

    const recalcPosition = () => {
        if (!tooltipRef.current) return;

        const pad = backdropOptions?.pad ?? 3;
        const outerPad = backdropOptions?.outerPad ?? 10;
        const offsetX = backdropOptions?.offsetX ?? 0;
        const offsetY = backdropOptions?.offsetY ?? 0;
        const backdropOuter = pad + 2 + outerPad;

        const target = targetEl.getBoundingClientRect();
        const tooltip = tooltipRef.current.getBoundingClientRect();

        let left: number;
        let top: number;

        switch (position) {
            case 'left':
                left = target.left - backdropOuter - tooltip.width - GAP + offsetX;
                top = target.top + target.height / 2 - tooltip.height / 2 + offsetY;
                break;
            case 'right':
                left = target.right + backdropOuter + GAP + offsetX;
                top = target.top + target.height / 2 - tooltip.height / 2 + offsetY;
                break;
            case 'top':
                left = target.left + target.width / 2 - tooltip.width / 2 + offsetX;
                top = target.top - backdropOuter - tooltip.height - GAP + offsetY;
                break;
            case 'bottom':
            default:
                left = target.left + target.width / 2 - tooltip.width / 2 + offsetX;
                top = target.bottom + backdropOuter + GAP + offsetY;
                break;
        }

        left = Math.max(MARGIN, Math.min(left, window.innerWidth - tooltip.width - MARGIN));
        top = Math.max(MARGIN, Math.min(top, window.innerHeight - tooltip.height - MARGIN));

        setStyle({ left, top, visibility: 'visible' });
    };

    useLayoutEffect(() => {
        recalcPosition();
    }, [targetEl, position]);

    useEffect(() => {
        const ro = new ResizeObserver(recalcPosition);
        ro.observe(targetEl);
        ro.observe(document.documentElement);
        return () => ro.disconnect();
    }, [targetEl, position]);

    const showButton =
        (behavior.type === 'action' && behavior.label) ||
        behavior.type === 'next' ||
        behavior.type === 'ok';

    return (
        <div ref={tooltipRef} className={cls.tooltip} style={style} data-pos={position}>
            <UiButton
                className={cls.closeBtn}
                variant="icon"
                startIcon={<Icon iconName="close" fill="currentColor" />}
                onClick={onDismiss}
            />

            <UiText weight="bold" classes={cls.text} variant="mediumPlain">
                {t(text)}
            </UiText>
            {showButton && (
                <UiButton
                    btnText={t(behavior.label)}
                    onClick={onAction}
                    className={cls.actionBtn}
                />
            )}
        </div>
    );
};
